import { client as apollo } from "@/graphql/vue-apollo";
import { unwrapNodes } from "@/helpers/graphql";
import cloneDeep from "lodash-es/cloneDeep";
import isEmpty from "lodash-es/isEmpty";
import isNull from "lodash-es/isNull";
import * as Sentry from "@sentry/vue";
import { businessLogic } from "@/config";

import addToCartMutation from "@/graphql/mutations/AddToCart.gql";
import removeFromCartMutation from "@/graphql/mutations/RemoveFromCart.gql";
import updateItemInCartMutation from "@/graphql/mutations/UpdateInCart.gql";
import cartQuery from "@/graphql/queries/Cart.gql";
import createCartMutation from "@/graphql/mutations/CreateCart.gql";

import { addToCartEvent, publishShopifyAddToCartEvent } from "@/helpers/events";
import { trackPolarAddToCart } from "@/helpers/polar";
import {
  markCartAddedOnCurrentPage,
  markCartVisitedThisSession,
} from "@/helpers/cartReminder.js";

// Attributes the theme adds for its own bookkeeping; they differ between
// page views and must not stop two adds of the same variant from merging.
const INTERNAL_LINE_ATTRIBUTES = ["_es_id"];

const comparableAttributes = (attributes = []) =>
  attributes
    .filter(({ key }) => !INTERNAL_LINE_ATTRIBUTES.includes(key))
    .map(({ key, value }) => `${key}=${value}`)
    .sort()
    .join("&");

/**
 * Finds a cart line that a new add of the same variant should merge into:
 * same variant, same selling plan state, same non-internal attributes.
 * Selling-plan (preorder) adds are never merged because the cart line does
 * not expose the selling plan id to compare against.
 */
export const findMergeableLine = (
  lines,
  { variantId, sellingPlanId, attributes }
) => {
  if (sellingPlanId) {
    return null;
  }

  const wanted = comparableAttributes(attributes);

  return (
    lines.find(
      (line) =>
        line.merchandise?.id === variantId &&
        !line.sellingPlanAllocation &&
        comparableAttributes(line.attributes) === wanted
    ) || null
  );
};

const state = {
  cart: null,
  isCartOpen: false,
  multipassCheckoutUrl: null,
  cartWarnings: "",
};

const mutations = {
  setCartState(state, value) {
    state.cart = value;
  },
  setCartOpenState(state, value) {
    state.isCartOpen = value;
  },
  setMultipassCheckoutUrl(state, value) {
    state.multipassCheckoutUrl = value;
  },
  setCartWarnings(state, value) {
    state.cartWarnings = value;
  },
};

const getters = {
  cartId: (state) => {
    return state.cart?.id || null;
  },
  lines: (state) => {
    const lines = unwrapNodes(state.cart?.lines?.edges) || [];
    return cloneDeep(lines);
  },
  subtotal: (state) => {
    return state.cart?.estimatedCost?.subtotalAmount || null;
  },
  lineItemsCount: (_, getters) => {
    return getters.lines.length || 0;
  },
};

const actions = {
  setCartWarnings({ commit }, value) {
    commit("setCartWarnings", value);
  },
  async checkCartRules({ commit, getters }, { productId, sellingPlanId }) {
    const { lines } = getters;

    const showCartWarning = (message) => {
      commit("setCartWarnings", message);
      return false;
    };

    // Helper to calculate total cart quantity
    const getTotalQuantity = () =>
      lines.reduce((total, { quantity }) => total + quantity, 0);

    // Helper to calculate product-specific quantities
    const getQuantityByProduct = () => {
      return lines.reduce(
        (
          acc,
          {
            merchandise: {
              product: { id },
            },
            quantity,
          }
        ) => {
          const product = acc.find((item) => item.id === id);
          if (product) {
            product.quantity += quantity;
          } else {
            acc.push({ id, quantity });
          }
          return acc;
        },
        []
      );
    };

    // Check if cart exceeds total quantity allowed
    if (!isNull(businessLogic.maxCartTotalQuantity)) {
      const totalQuantity = getTotalQuantity();
      if (totalQuantity >= businessLogic.maxCartTotalQuantity) {
        return showCartWarning(
          "You have reached the maximum quantity of products allowed in the cart. Your latest item was not added."
        );
      }
    }

    // Check if cart exceeds product-specific quantity allowed
    if (!isNull(businessLogic.maxCartProductQuantity)) {
      const productQuantities = getQuantityByProduct();
      const product = productQuantities.find((item) => item.id === productId);

      if (product?.quantity >= businessLogic.maxCartProductQuantity) {
        return showCartWarning(
          "You have reached the maximum quantity allowed of this product in the cart. Your latest item was not added."
        );
      }
    }

    // Preorders must be purchased on their own: they can only be added to an
    // empty bag, and no other items can be added once a preorder is in the bag.
    if (businessLogic.preventSplitCart && lines.length > 0) {
      const isAddingPreorder = !!sellingPlanId;
      const hasPreorderInCart = lines.some(
        (line) => line.sellingPlanAllocation
      );

      if (isAddingPreorder) {
        return showCartWarning(
          "Preorder items must be purchased on their own. Please empty your bag before adding a preorder."
        );
      }

      if (hasPreorderInCart) {
        return showCartWarning(
          "Preorder items must be purchased on their own. Please checkout your preorder separately before adding other items."
        );
      }
    }

    commit("setCartWarnings", "");
    return true;
  },
  async addCartItem(
    { commit, dispatch, state, rootState, rootGetters, getters },
    { productId, variantId, quantity, sellingPlanId, attributes = [] }
  ) {
    if (rootGetters["product/isPreLaunch"]) {
      return;
    }

    const canAdd = await dispatch("checkCartRules", {
      productId,
      sellingPlanId,
    });

    if (!canAdd) {
      return;
    }

    let es_id = "-1";
    try {
      // eslint-disable-next-line no-undef
      es_id = EasySizeParametersDebug.easysize.pageview_id.toString();
    } catch (e) {
      console.log("EasySizeParametersDebug: " + e);
    }

    const newAttributes = [...attributes, { key: "_es_id", value: es_id }];

    // Runs after the cart has been updated: tracking and wishlist clean-up.
    // Nothing in here may block or break the cart UI, so tracking failures
    // are swallowed (addToCartEvent is async, hence the .catch rather than
    // a try/catch, which would not catch a rejected promise).
    const afterAdd = (cart) => {
      markCartAddedOnCurrentPage();

      addToCartEvent({
        sku: variantId,
        quantity: quantity,
        cart,
      }).catch((err) => {
        console.warn("[Tracking] add to cart event failed:", err);
      });

      try {
        const cartLines = unwrapNodes(cart.lines?.edges);
        if (!cartLines) {
          return;
        }

        const addedLine = cartLines.find(
          (line) => line.merchandise.id === variantId
        );
        const addedProductId = addedLine?.merchandise.product.id;
        if (
          !addedProductId ||
          !rootState.product.wishlist.includes(addedProductId)
        ) {
          return;
        }

        dispatch("product/removeFromWishList", addedProductId, {
          root: true,
        });
      } catch (err) {
        console.log(err);
      }
    };

    // Shopify only merges cart lines whose attributes match exactly, and the
    // `_es_id` attribute changes on every page view, so re-adding a variant
    // the customer already has would create a second line for the same item.
    // Bump the quantity on the existing line instead.
    const existingLine = findMergeableLine(getters.lines, {
      variantId,
      sellingPlanId,
      attributes,
    });

    if (existingLine) {
      try {
        const { data } = await apollo.mutate({
          mutation: updateItemInCartMutation,
          variables: {
            cartId: state.cart.id,
            lines: [
              {
                id: existingLine.id,
                merchandiseId: variantId,
                quantity: existingLine.quantity + quantity,
              },
            ],
          },
        });

        commit("setCartState", data.cartLinesUpdate.cart);
        afterAdd(data.cartLinesUpdate.cart);
        return;
      } catch (error) {
        // Fall through to a normal add, which also recreates a missing cart.
        console.warn("Cart: could not merge into existing line, adding", error);
      }
    }

    // We retry adding the cart item if when a user tries adding an item to the cart, the mutation fails.
    // EG where the cart no longer exists on the backend for whatever reason, the mutation would return an error.
    // The catch creates the cart, and then the while loop trys adding the item to cart
    // We limit the maxTries to 2 so we don't get an infinite loop.
    let count = 0;
    const maxTries = 2;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const { data } = await apollo.mutate({
          mutation: addToCartMutation,
          variables: {
            cartId: state.cart.id,
            lines: [
              {
                quantity,
                merchandiseId: variantId,
                attributes: newAttributes,
                sellingPlanId,
              },
            ],
          },
        });

        commit("setCartState", data.cartLinesAdd.cart);
        afterAdd(data.cartLinesAdd.cart);
        return;
      } catch (error) {
        if (++count === maxTries) {
          console.error("Cart: Item couldn't be added to cart", error);
          return;
        }
        console.warn("Cart: Cart could not be fetched, creating new cart");
        await dispatch("createCart");
      }
    }
  },
  async removeCartItem({ commit, state }, cardLineId) {
    const cartId = state.cart.id;

    try {
      const { data } = await apollo.mutate({
        mutation: removeFromCartMutation,
        variables: {
          cartId,
          lineIds: [cardLineId],
        },
      });

      commit("setCartState", data.cartLinesRemove.cart);
    } catch (error) {
      console.log(error);
    }
  },
  async updateCartItem({ state, commit, getters }, line) {
    const cartId = state.cart.id;

    // Get the current quantity before update to calculate delta for analytics
    const existingLine = getters.lines.find((l) => l.id === line.id);
    const previousQuantity = existingLine?.quantity || 0;

    try {
      const { data } = await apollo.mutate({
        mutation: updateItemInCartMutation,
        variables: {
          cartId,
          lines: [line],
        },
      });

      commit("setCartState", data.cartLinesUpdate.cart);

      // Fire analytics event if quantity increased (not for decreases or removals)
      const newQuantity = line.quantity;
      const quantityDelta = newQuantity - previousQuantity;

      if (quantityDelta > 0 && existingLine) {
        try {
          const { merchandise } = existingLine;
          const images = unwrapNodes(merchandise.product.images?.edges);
          const imageUrl =
            images?.[0]?.klaviyo || images?.[0]?.largeMedia || "";

          const variantTitle =
            merchandise.selectedOptions?.map((opt) => opt.value).join(" / ") ||
            "";

          publishShopifyAddToCartEvent({
            variantId: merchandise.id,
            productId: merchandise.product.id,
            productTitle: merchandise.product.title,
            productType: merchandise.product.productType,
            productUrl: merchandise.product.onlineStoreUrl,
            variantTitle: variantTitle,
            sku: merchandise.sku || "",
            unitPrice: parseFloat(merchandise.priceV2.amount),
            currencyCode: merchandise.priceV2.currencyCode,
            quantity: quantityDelta, // Only the amount being added, not total
            imageUrl: imageUrl,
            vendor: merchandise.product.vendor,
          });

          await trackPolarAddToCart({
            variantId: merchandise.id,
            productId: merchandise.product.id,
            productTitle: merchandise.product.title,
            productType: merchandise.product.productType,
            productUrl: merchandise.product.onlineStoreUrl,
            variantTitle: variantTitle,
            sku: merchandise.sku || "",
            unitPrice: parseFloat(merchandise.priceV2.amount),
            currencyCode: merchandise.priceV2.currencyCode,
            quantity: quantityDelta, // Only the amount being added, not total
            imageUrl: imageUrl,
            vendor: merchandise.product.vendor,
          });
        } catch (err) {
          console.log(
            "[Analytics] Error publishing quantity increase event:",
            err
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  },
  async createCart({ commit, dispatch }) {
    try {
      const { data } = await apollo.mutate({
        mutation: createCartMutation,
      });

      localStorage.setItem("shopifyCartId", data.cartCreate.cart.id);
      commit("setCartState", data.cartCreate.cart);

      dispatch("multipassCheckoutUrl", null, { root: true });
    } catch (error) {
      console.log(error);
    }
  },
  async fetchCart({ commit, dispatch }) {
    const localStorageCartId = localStorage.getItem("shopifyCartId");

    if (!localStorageCartId) {
      console.log("Cart: Cart ID not set in location storage");
      dispatch("createCart");
      return;
    }

    try {
      const { data } = await apollo.query({
        query: cartQuery,
        variables: {
          id: localStorageCartId,
        },
      });

      if (!data.cart) {
        console.warn("Cart: Cart null, creating new cart");
        dispatch("createCart");
        return;
      }

      commit("setCartState", data.cart);

      dispatch("multipassCheckoutUrl", null, { root: true });
    } catch (error) {
      console.warn("Cart: Cart could not be fetched, creating new cart");
      dispatch("createCart");
    }
  },
  setCartOpen({ commit }, value) {
    if (value) {
      markCartVisitedThisSession();
    }
    commit("setCartOpenState", value);
  },
  multipassCheckoutUrl: {
    root: true,
    async handler({ commit, state, rootState }) {
      const email = rootState.app.customerEmail;

      if (!email) {
        return false;
      }

      const checkoutUrl = state.cart.checkoutUrl;
      const domain = new URL(checkoutUrl);
      const pathQuery = domain.pathname + domain.search;

      const input = {
        email: rootState.app.customerEmail,
        redirectUrl: pathQuery,
      };

      Sentry.addBreadcrumb({
        category: "auth",
        message: "multipassCheckoutUrl input " + JSON.stringify(input),
        level: "info",
      });

      const multipassResponse = await fetch(
        process.env.RATANDBOA_NODE_APP_URL +
          "/multipass?" +
          new URLSearchParams(input)
      );

      Sentry.addBreadcrumb({
        category: "auth",
        message:
          "multipassAccount response " + JSON.stringify(multipassResponse),
        level: "info",
      });

      const multipassData = await multipassResponse.json();
      if (isEmpty(multipassData)) {
        Sentry.captureMessage(
          "Empty multipassData cart: " + JSON.stringify(multipassData)
        );
        return;
      }

      commit("setMultipassCheckoutUrl", multipassData.url);
    },
  },
  applyDiscount() {
    const urlParams = new URLSearchParams(window.location.search);
    const discountCode = urlParams.get("discount");
    if (discountCode) {
      document.cookie = `discount_code=${discountCode}; path=/`;
      fetch(`/discount/${discountCode}`).catch(function (error) {
        console.error("Error:", error);
      });
    }
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
